/**
 * Bluebell — quiz engine
 * assets/bluebell-quiz-engine.js
 *
 * Written once, shared by every quiz. Knows nothing about hair or skin.
 *
 * Questions, answers and products come from theme blocks (config island).
 * Result copy comes from locale files.
 * Recommendation logic comes from a per-quiz rules file that registers
 * itself on window.QuizRules — see quiz-rules-hair.js.
 *
 * Flow: intro → questions → gate (name + email + phone) → result
 * The single save to Shopify happens when the gate is passed, which is the
 * moment everything is known: her contact details and her completed answers.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  var CART_ICON =
    '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2.2" aria-hidden="true" focusable="false"><circle cx="9" cy="21" r="1"/>' +
    '<circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>';

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Accepts what Egyptian customers actually type, including Arabic-Indic digits. */
  function toLatinDigits(value) {
    return String(value)
      .replace(/[٠-٩]/g, function (d) { return String(d.charCodeAt(0) - 0x0660); })
      .replace(/[۰-۹]/g, function (d) { return String(d.charCodeAt(0) - 0x06F0); });
  }

  function normalizePhone(raw) {
    var s = toLatinDigits(raw).replace(/[^\d+]/g, '').replace(/^\+/, '');
    if (s.indexOf('0020') === 0) s = s.slice(4);
    else if (s.indexOf('20') === 0 && s.length > 11) s = s.slice(2);
    if (s.charAt(0) !== '0') s = '0' + s;
    if (!/^01[0125][0-9]{8}$/.test(s)) return null;
    return { local: s, e164: '+20' + s.slice(1), intl: '20' + s.slice(1) };
  }

  /* One line, single spaces, at most 40 characters, and at least one letter
     in any script. The first name goes into a WhatsApp template variable,
     which Meta rejects if it holds a line break, a tab or a run of spaces. */
  function cleanName(raw) {
    var s = String(raw || '').replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
    return /\p{L}/u.test(s) ? s : '';
  }

  class BluebellQuiz extends HTMLElement {
    connectedCallback() {
      var island = this.querySelector('[data-quiz-config]');
      if (!island) return;

      try {
        this.config = JSON.parse(island.textContent);
      } catch (e) {
        console.error('[quiz] config could not be parsed', e);
        return;
      }

      /* Where the words come from, in order:

           1. assets/quiz-copy-<quiz>.js   the source of truth
           2. theme editor block text      only for keys that file is missing
           3. locale files                 last resort, so nothing is ever blank

         Delete the copy file and the quiz drops back to 2 and 3, which is how
         it behaved before that file existed. Nothing breaks. */
      var copySet = (window.QuizCopy || {})[this.config.quiz] || {};
      var lang = String(this.config.lang || 'en').slice(0, 2).toLowerCase();

      this.copy = copySet[lang] || copySet.en || {};
      this.locale = this.config.strings || {};
      this.strings = Object.assign({}, this.locale, this.copy);

      this.applyCopy();

      this.products = this.config.products || {};
      this.questions = this.buildQuestions();

      if (!this.questions.length) {
        console.warn('[quiz] no question blocks — add them in the theme editor');
        return;
      }

      this.rules = (window.QuizRules || {})[this.config.quiz];
      if (typeof this.rules !== 'function') {
        console.error('[quiz] no rules registered for "' + this.config.quiz +
                      '". Is assets/quiz-rules-' + this.config.quiz + '.js loaded?');
        return;
      }

      this.answers = {};
      this.index = -1;
      this.lastResult = { title: '', concerns: [], steps: [] };
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.phone = null;
      this.saved = false;

      this.refs = {
        progress:    this.querySelector('[data-progress]'),
        progressBar: this.querySelector('[data-progress-track]'),
        progressText:this.querySelector('[data-progress-label]'),
        screens: {
          intro:    this.querySelector('[data-screen="intro"]'),
          question: this.querySelector('[data-screen="question"]'),
          gate:     this.querySelector('[data-screen="gate"]'),
          result:   this.querySelector('[data-screen="result"]')
        },
        qEyebrow: this.querySelector('[data-q-eyebrow]'),
        qTitle:   this.querySelector('[data-q-title]'),
        qOptions: this.querySelector('[data-q-options]'),
        back:     this.querySelector('[data-back]'),
        next:     this.querySelector('[data-next]'),
        gateFirst:   this.querySelector('[data-gate-first]'),
        gateLast:    this.querySelector('[data-gate-last]'),
        gateEmail:   this.querySelector('[data-gate-email]'),
        gatePhone:   this.querySelector('[data-gate-phone]'),
        gateConsent: this.querySelector('[data-gate-consent]'),
        gateError:   this.querySelector('[data-gate-error]'),
        gateBack:    this.querySelector('[data-gate-back]'),
        title:    this.querySelector('[data-result-title]'),
        summary:  this.querySelector('[data-result-summary]'),
        traits:   this.querySelector('[data-traits]'),
        concerns: this.querySelector('[data-concerns]'),
        why:      this.querySelector('[data-why]'),
        routine:  this.querySelector('[data-routine]'),
        confirm:  this.querySelector('[data-confirm]'),
        leadForm: this.querySelector('form.quiz__lead-form'),
        leadSink: this.querySelector('[data-lead-sink]')
      };

      /* The navy panel around the confirmation line has nothing else in it,
         so it is hidden until there is something to confirm. Otherwise a
         result opened from a link (the WhatsApp button) shows an empty bar. */
      this.refs.leadBox = this.refs.confirm && this.refs.confirm.closest('.quiz__lead');
      if (this.refs.leadBox) this.refs.leadBox.hidden = true;

      this.buildProgress();
      this.bind();
      this.warnOnUnknownCodes();
      this.restoreFromUrl();
    }

    /* The answer codes are the contract between the theme blocks and the rules
       file. A renamed code fails silently at runtime, so say so loudly here. */
    warnOnUnknownCodes() {
      var expected = (window.QuizCodes || {})[this.config.quiz];
      if (!expected) return;

      var self = this;
      this.questions.forEach(function (q) {
        var want = expected[q.key];
        if (!want) {
          console.warn('[quiz] question code "' + q.key + '" is not used by the ' +
                       self.config.quiz + ' rules — its answers will not affect the result');
          return;
        }
        q.answers.forEach(function (opt) {
          if (want.indexOf(opt.value) === -1) {
            console.warn('[quiz] answer code "' + opt.value + '" on question "' + q.key +
                         '" is not used by the rules — expected one of: ' + want.join(', '));
          }
        });
      });
    }

    /* Question order and codes come from window.QuizCodes in the rules file.
       Those are logic, not words, and must never be translated: Translate &
       Adapt once turned the "dandruff" code into Arabic, which silently
       stopped every dandruff recommendation from firing on the Arabic page and
       corrupted the result code in the URL. Reading them from the rules file
       makes that impossible.

       Only the wording is looked up, copy file first, then the block, then the
       locale file. If the rules file is missing its codes, the blocks are used
       whole, exactly as before. */
    buildQuestions() {
      var self = this;
      var blocks = this.config.questions || [];
      var codes = (window.QuizCodes || {})[this.config.quiz];
      if (!codes) return blocks;

      var byKey = {};
      blocks.forEach(function (b) { byKey[b.key] = b; });

      function word(key, blockValue) {
        return self.copy[key] || blockValue || self.locale[key] || '';
      }

      return Object.keys(codes).map(function (key) {
        var block = byKey[key] || {};
        var blockAnswers = block.answers || [];

        return {
          key: key,
          eyebrow: word('q.' + key + '.eyebrow', block.eyebrow),
          title: word('q.' + key + '.title', block.title),
          answers: codes[key].map(function (value) {
            var match = blockAnswers.filter(function (a) { return a.value === value; })[0];
            return {
              value: value,
              label: word('q.' + key + '.' + value, match && match.label) || value
            };
          })
        };
      });
    }

    /* The intro, the gate and the result headings are printed into the page by
       Liquid, straight from the locale files, so they are on screen before any
       script runs. That is deliberate: the text is right with JavaScript off,
       and it is what stays if quiz-copy-<quiz>.js is ever deleted.

       When the copy file is there it wins, so those nodes are rewritten here.
       data-copy holds the key for an element's text; data-copy-attr holds
       "attribute:key" for placeholders and labels. A key the copy file does
       not have is left alone, so it keeps whatever Liquid rendered. */
    applyCopy() {
      var self = this;
      if (!this.copy || !Object.keys(this.copy).length) return;

      this.querySelectorAll('[data-copy]').forEach(function (el) {
        var value = self.copy[el.getAttribute('data-copy')];
        if (value) el.textContent = value;
      });

      this.querySelectorAll('[data-copy-attr]').forEach(function (el) {
        el.getAttribute('data-copy-attr').split(',').forEach(function (pair) {
          var at = pair.indexOf(':');
          if (at < 0) return;
          var value = self.copy[pair.slice(at + 1).trim()];
          if (value) el.setAttribute(pair.slice(0, at).trim(), value);
        });
      });
    }

    /* ---------------- i18n ---------------- */

    t(key, vars) {
      var out = this.strings[key];
      if (out == null) return '';
      if (vars) {
        Object.keys(vars).forEach(function (name) {
          out = out.split('[' + name + ']').join(vars[name]);
        });
      }
      return out;
    }

    productUrl(key) {
      var p = this.products[key];
      return p && p.url ? p.url : '';
    }

    productVariant(key) {
      var p = this.products[key];
      return p && p.variant ? String(p.variant) : '';
    }

    /* ---------------- shareable result links ----------------
       Answers ride in the URL in question order. Nothing is stored server
       side — the link is the record. Opening one shows that result and
       deliberately skips the gate: whoever has the link already has it.

       resultCode() is the bare code, without a path. The WhatsApp template's
       button carries exactly this string as its dynamic URL suffix, so it has
       to be available on its own, not only baked into a path. */

    resultCode() {
      var self = this;
      var parts = this.questions.map(function (q) { return self.answers[q.key]; });
      if (parts.some(function (p) { return !p; })) return '';
      return parts.join('-');
    }

    resultPath() {
      var code = this.resultCode();
      return code ? location.pathname + '?r=' + code : '';
    }

    resultUrl() {
      var path = this.resultPath();
      return path ? location.origin + path : '';
    }

    restoreFromUrl() {
      var raw = new URLSearchParams(location.search).get('r');
      if (!raw) return;

      var parts = raw.split('-');

      /* A trailing language, "...-frizzy-ar", comes from the WhatsApp button.
         Both templates' URLs point at the English page (the URL is frozen in
         the approved template), so the Arabic one gets "-ar" added by the
         Cloud Function and lands here. Send her to the same result on the page
         in her language; replace() keeps the detour out of the back button.
         Every other link still has exactly six parts and is untouched. */
      if (parts.length === this.questions.length + 1 && /^(ar|en)$/.test(parts[parts.length - 1])) {
        var want = parts.pop();
        var here = String(this.config.lang || 'en').slice(0, 2).toLowerCase();
        if (want !== here) {
          var path = location.pathname.replace(/^\/ar(?=\/)/, '');
          if (want === 'ar') path = '/ar' + path;
          location.replace(path + '?r=' + parts.join('-'));
          return;
        }
      }

      if (parts.length !== this.questions.length) return;

      var answers = {};
      for (var i = 0; i < this.questions.length; i++) {
        var q = this.questions[i];
        var valid = q.answers.some(function (o) { return o.value === parts[i]; });
        if (!valid) return;
        answers[q.key] = parts[i];
      }

      this.answers = answers;
      this.index = this.questions.length - 1;
      this.renderResult();
    }

    /* ---------------- wiring ---------------- */

    buildProgress() {
      var track = this.refs.progressBar;
      if (!track) return;
      track.innerHTML = '';
      for (var i = 0; i < this.questions.length; i++) {
        var step = document.createElement('span');
        step.className = 'quiz__progress-step';
        track.appendChild(step);
      }
      this.refs.progress.setAttribute('aria-valuemax', String(this.questions.length));
    }

    bind() {
      var self = this;
      this.querySelector('[data-start]').addEventListener('click', function () { self.start(); });
      this.refs.next.addEventListener('click', function () { self.next(); });
      this.refs.back.addEventListener('click', function () { self.previous(); });
      this.refs.gateBack.addEventListener('click', function () { self.backToQuestions(); });
      this.querySelector('[data-gate-submit]').addEventListener('click', function () { self.submitGate(); });
      this.querySelector('[data-restart]').addEventListener('click', function () { self.restart(); });

      this.refs.gatePhone.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); self.submitGate(); }
      });

      this.addEventListener('click', function (e) {
        var link = e.target.closest && e.target.closest('[data-add-to-cart]');
        if (!link) return;
        e.preventDefault();
        self.addToCart(link);
      });
    }

    show(name) {
      var screens = this.refs.screens;
      Object.keys(screens).forEach(function (k) {
        if (screens[k]) screens[k].hidden = k !== name;
      });
    }

    /* ---------------- quiz flow ---------------- */

    start() {
      this.index = 0;
      this.show('question');
      this.refs.progress.hidden = false;
      this.renderQuestion();
    }

    renderQuestion() {
      var self = this;
      var q = this.questions[this.index];
      var total = this.questions.length;

      this.refs.qEyebrow.textContent = q.eyebrow || '';
      this.refs.qTitle.textContent = q.title || '';
      this.refs.progressText.textContent = this.t('progress', { current: this.index + 1, total: total });

      var steps = this.refs.progressBar.children;
      for (var i = 0; i < steps.length; i++) {
        steps[i].style.setProperty('--fill', i < this.index ? '100%' : (i === this.index ? '50%' : '0%'));
      }
      this.refs.progress.setAttribute('aria-valuenow', String(this.index + 1));

      this.refs.qOptions.innerHTML = '';
      q.answers.forEach(function (opt) {
        var label = document.createElement('label');
        label.className = 'quiz__option';

        var input = document.createElement('input');
        input.type = 'radio';
        input.name = self.config.uid + '-' + q.key;
        input.value = opt.value;
        input.className = 'quiz__radio';
        input.checked = self.answers[q.key] === opt.value;
        input.addEventListener('change', function () {
          self.answers[q.key] = opt.value;
          self.refs.next.disabled = false;
          self.refs.qOptions.querySelectorAll('.quiz__option').forEach(function (el) {
            el.classList.toggle('is-selected', el.contains(input));
          });
        });

        var dot = document.createElement('span');
        dot.className = 'quiz__dot';
        dot.setAttribute('aria-hidden', 'true');

        var text = document.createElement('span');
        text.textContent = opt.label;

        label.append(input, dot, text);
        if (input.checked) label.classList.add('is-selected');
        self.refs.qOptions.appendChild(label);
      });

      this.refs.back.hidden = this.index === 0;
      this.refs.next.disabled = !this.answers[q.key];
      this.refs.next.textContent = this.index === total - 1 ? this.t('nav.finish') : this.t('nav.next');

      var first = this.refs.qOptions.querySelector('input');
      if (first) first.focus({ preventScroll: true });
    }

    next() {
      if (this.index < this.questions.length - 1) {
        this.index += 1;
        this.renderQuestion();
      } else {
        this.showGate();
      }
    }

    previous() {
      if (this.index > 0) {
        this.index -= 1;
        this.renderQuestion();
      }
    }

    backToQuestions() {
      this.show('question');
      this.refs.progress.hidden = false;
      this.renderQuestion();
    }

    restart() {
      this.answers = {};
      this.index = -1;
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.phone = null;
      this.saved = false;

      this.refs.progress.hidden = true;
      if (this.refs.gateFirst) this.refs.gateFirst.value = '';
      if (this.refs.gateLast) this.refs.gateLast.value = '';
      this.refs.gateEmail.value = '';
      this.refs.gatePhone.value = '';
      if (this.refs.gateConsent) this.refs.gateConsent.checked = false;
      this.refs.gateError.hidden = true;
      this.refs.confirm.hidden = true;
      if (this.refs.leadBox) this.refs.leadBox.hidden = true;

      this.show('intro');
      this.querySelector('[data-start]').focus({ preventScroll: true });
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------------- the gate ----------------
       She has answered everything but has not seen her result. Contact
       details are collected here so the record is complete in one write. */

    showGate() {
      this.show('gate');
      this.refs.progress.hidden = true;
      (this.refs.gateFirst || this.refs.gateEmail).focus({ preventScroll: true });
    }

    gateFail(message, field) {
      this.refs.gateError.textContent = message;
      this.refs.gateError.hidden = false;
      if (field) field.focus();
    }

    submitGate() {
      /* The name fields are skipped only if the section on the page is an
         older copy without them, so a half-updated theme still works. */
      var first = '', last = '';
      if (this.refs.gateFirst) {
        first = cleanName(this.refs.gateFirst.value);
        if (!first) return this.gateFail(this.t('gate.err_first_name'), this.refs.gateFirst);
      }
      if (this.refs.gateLast) {
        last = cleanName(this.refs.gateLast.value);
        if (!last) return this.gateFail(this.t('gate.err_last_name'), this.refs.gateLast);
      }

      var email = (this.refs.gateEmail.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        return this.gateFail(this.t('gate.err_email'), this.refs.gateEmail);
      }

      var phone = normalizePhone(this.refs.gatePhone.value);
      if (!phone) {
        return this.gateFail(this.t('gate.err_phone'), this.refs.gatePhone);
      }

      /* The checkbox is gone - the notice above the button does that job.
         Still honoured if an older copy of the section has one. */
      if (this.refs.gateConsent && !this.refs.gateConsent.checked) {
        return this.gateFail(this.t('gate.err_consent'), this.refs.gateConsent);
      }

      this.refs.gateError.hidden = true;
      this.firstName = first;
      this.lastName = last;
      this.email = email;
      this.phone = phone;
      this.refs.gatePhone.value = phone.local;

      /* WhatsApp first: window.open only works inside the click. */
      this.renderResult();
      this.handoffToWhatsApp();
      this.saveLead();
    }

    /* ---------------- result ---------------- */

    renderResult() {
      var self = this;

      var api = {
        t: function (key, vars) { return self.t(key, vars); },
        product: function (key) { return { url: self.productUrl(key), variant: self.productVariant(key) }; },
        link: function (key, label) {
          var url = self.productUrl(key);
          return url ? '<a href="' + url + '">' + escapeHtml(label) + '</a>' : escapeHtml(label);
        }
      };

      var data = this.rules(this.answers, api);
      this.lastResult = data;

      this.show('result');
      this.refs.progress.hidden = true;

      this.refs.title.textContent = data.title || '';
      this.refs.summary.textContent = data.summary || '';

      this.refs.traits.innerHTML = '';
      (data.traits || []).forEach(function (row) {
        var el = document.createElement('div');
        el.className = 'quiz__trait';
        el.innerHTML = '<span class="quiz__trait-label">' + escapeHtml(row.label) +
          '</span><span class="quiz__trait-value">' + escapeHtml(row.value) + '</span>';
        self.refs.traits.appendChild(el);
      });

      this.refs.concerns.innerHTML = '';
      (data.concerns || []).forEach(function (c) {
        var li = document.createElement('li');
        li.className = 'quiz__tag';
        li.textContent = c;
        self.refs.concerns.appendChild(li);
      });

      this.refs.why.innerHTML = '';
      (data.reasons || []).forEach(function (r) {
        var li = document.createElement('li');
        li.className = 'quiz__why';
        li.innerHTML = '<b>' + escapeHtml(r.label) + ':</b> ' + escapeHtml(r.text);
        self.refs.why.appendChild(li);
      });

      var brands = (this.config.brandNames || [])
        .map(function (s) { return String(s).trim(); })
        .filter(Boolean);
      function linkifyTitle(title, url) {
        var safe = escapeHtml(title);
        if (!url) return safe;
        for (var i = 0; i < brands.length; i++) {
          if (safe.indexOf(brands[i]) !== -1) {
            return safe.replace(brands[i], '<a href="' + url + '">' + brands[i] + '</a>');
          }
        }
        return safe;
      }

      this.refs.routine.innerHTML = '';
      (data.steps || []).forEach(function (s, i) {
        var li = document.createElement('li');
        li.className = 'quiz__step';
        var cart = s.url
          ? '<a class="quiz__cart" href="' + s.url + '" data-add-to-cart data-variant="' +
            escapeHtml(s.variant || '') + '">' + CART_ICON + escapeHtml(self.t('cart.add')) + '</a>'
          : '';
        li.innerHTML =
          '<span class="quiz__step-num" aria-hidden="true">' + (i + 1) + '</span>' +
          '<div class="quiz__step-body">' +
            '<h4 class="quiz__step-title">' + linkifyTitle(s.title, s.url) + '</h4>' +
            '<p class="quiz__step-text">' + s.text + '</p>' +
            '<span class="quiz__freq">' + escapeHtml(s.freq) + '</span>' + cart +
          '</div>';
        self.refs.routine.appendChild(li);
      });

      this.refs.title.focus({ preventScroll: true });
    }

    /* ---------------- cart ---------------- */

    refreshCart(opener) {
      var self = this;
      var drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.refresh === 'function') {
        var reveal = function () {
          if (self.config.openCartDrawer === false) return;
          if (typeof drawer.open !== 'function') return;
          try { drawer.open(opener); } catch (e) { /* drawer stays closed */ }
        };
        try {
          var pending = drawer.refresh();
          if (pending && typeof pending.then === 'function') pending.then(reveal, reveal);
          else reveal();
          return;
        } catch (e) { /* fall through */ }
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    }

    addToCart(link) {
      var self = this;
      var href = link.getAttribute('href');
      var variant = link.getAttribute('data-variant');
      if (!variant) { window.location.href = href; return; }

      var original = link.innerHTML;
      link.textContent = this.t('cart.adding');

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('cart add failed'); return r.json(); })
        .then(function () {
          link.textContent = self.t('cart.added');
          self.refreshCart(link);
          setTimeout(function () { link.innerHTML = original; }, 2500);
        })
        .catch(function () {
          link.innerHTML = original;
          window.location.href = href;
        });
    }

    /* ---------------- lead ---------------- */

    answersSummary() {
      var self = this;
      return this.questions.map(function (q) {
        var value = self.answers[q.key];
        if (!value) return null;
        var chosen = null;
        q.answers.forEach(function (o) { if (o.value === value) chosen = o.label; });
        return '• ' + (q.eyebrow || q.key) + ': ' + (chosen || value);
      }).filter(Boolean).join('\n');
    }

    buildNote() {
      var self = this;
      var lines = [this.lastResult.title + ' | ' + (this.lastResult.concerns || []).join(', '), ''];
      lines.push(this.answersSummary());

      if ((this.lastResult.steps || []).length) {
        lines.push('', this.t('note.routine'));
        this.lastResult.steps.forEach(function (s, i) {
          lines.push((i + 1) + '. ' + s.title + ' — ' + s.freq);
        });
      }

      var url = this.resultUrl();
      if (url) lines.push('', 'Report: ' + url);
      return lines.join('\n');
    }

    /* One write, at the gate. Everything is known by then.

       Posts into the hidden frame so she keeps her result on screen. */
    saveLead() {
      if (this.saved) return;
      var form = this.refs.leadForm;
      if (!form || !this.email) return;
      this.saved = true;

      /* Same-origin, so verifySave() can read the frame afterwards. */
      var back = form.querySelector('[data-field-return]');
      var path = this.resultPath();
      if (back && path) back.value = path;

      form.querySelector('[data-field-email]').value = this.email;
      form.querySelector('[data-field-phone]').value = this.phone ? this.phone.e164 : '';
      form.querySelector('[data-field-note]').value = this.buildNote();

      if (form.requestSubmit) form.requestSubmit();
      else form.submit();

      this.verifySave(path);
    }

    /* A frame post tells you nothing by itself: a rejected submission looks
       exactly like a successful one. But return_to is same-origin, so if the
       save landed the frame ends up somewhere we are allowed to read. If it
       does not, Shopify sent it to a challenge instead and the lead is gone.

       Nothing is shown to her either way - this only makes the loss findable
       in the console and countable in analytics. */
    verifySave(expectedPath) {
      var self = this;
      var frame = this.refs.leadSink;
      if (!frame || !expectedPath) return;

      var tries = 0;
      var timer = setInterval(function () {
        tries += 1;
        var landed = false;
        try {
          var href = frame.contentWindow.location.href;
          landed = href.indexOf(expectedPath.split('?')[0]) !== -1;
        } catch (e) {
          landed = false; // cross-origin: a challenge page
        }

        if (landed) {
          clearInterval(timer);
          if (window.dataLayer) window.dataLayer.push({ event: 'quiz_lead_saved', quiz: self.config.quiz });
          return;
        }

        if (tries >= 10) { // ~5s
          clearInterval(timer);
          console.warn('[quiz] lead NOT saved - Shopify did not return to ' + expectedPath +
                       '. Almost always hCaptcha on the customer form ' +
                       '(Online Store > Preferences > Spam protection).');
          if (window.dataLayer) window.dataLayer.push({ event: 'quiz_lead_failed', quiz: self.config.quiz });
        }
      }, 500);
    }

    whatsappMessage(intlPhone) {
      var parts = [this.t('wa.greeting'), ''];
      parts.push(this.t('wa.result') + this.lastResult.title);
      if ((this.lastResult.concerns || []).length) {
        parts.push(this.t('wa.concerns') + this.lastResult.concerns.join(', '));
      }
      parts.push('', this.t('wa.answers'), this.answersSummary());

      if ((this.lastResult.steps || []).length) {
        parts.push('', this.t('wa.routine'));
        this.lastResult.steps.forEach(function (s, i) {
          parts.push((i + 1) + '. ' + s.title + ' — ' + s.freq);
        });
      }

      var url = this.resultUrl();
      if (url) parts.push('', this.t('wa.report_link') + url);
      parts.push('', this.t('wa.my_number') + intlPhone, this.t('wa.closing'));
      return parts.join('\n');
    }

    handoffToWhatsApp() {
      if (!this.phone) return;

      if (this.config.whatsappMode === 'automated') {
        this.showConfirmation(false);
        this.sendReportAutomatically();
        return;
      }

      this.showConfirmation(true);
    }

    /* Automated mode. The Cloud Function holds the WhatsApp token and does the
       sending; nothing secret ever reaches the browser.

       Fire and forget. She is already looking at her result, so a slow or
       failed send must never block her or show her an error she cannot act on.
       Failures are recorded server side in the "quiz_sends" collection, and warned
       about here so they are findable in the console. */
    sendReportAutomatically() {
      var self = this;
      var endpoint = this.config.reportEndpoint;

      if (!endpoint) {
        console.warn('[quiz] whatsappMode is "automated" but no endpoint is set — ' +
                     'paste the sendReport URL into the section settings');
        return;
      }

      var code = this.resultCode();
      if (!code) return;

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quiz: self.config.quiz,
          r: code,
          phone: self.phone.intl,
          title: self.lastResult.title,
          firstName: self.firstName,
          lastName: self.lastName,
          email: self.email,
          lang: (document.documentElement.lang || 'en').slice(0, 2),

          /* Her concerns and routine, so the record kept against her Shopify
             customer reads the way the page did — in her language, with the
             products she was actually given. The result code alone could not
             say that without the rules and copy files being re-run server
             side. Only titles and frequencies: the long explanations stay on
             the page. */
          concerns: self.lastResult.concerns || [],
          steps: (self.lastResult.steps || []).map(function (s) {
            return { title: s.title, freq: s.freq };
          })
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'quiz_report_sent', quiz: self.config.quiz });
          }
        })
        .catch(function (err) {
          console.warn('[quiz] automated WhatsApp send failed', err);
          if (window.dataLayer) {
            window.dataLayer.push({ event: 'quiz_report_send_failed', quiz: self.config.quiz });
          }
        });
    }

    /* openWindow is false when we are redrawing this after the round trip:
       WhatsApp was already opened from her click, before we navigated. */
    showConfirmation(openWindow) {
      var automated = this.config.whatsappMode === 'automated';
      var phone = this.phone;
      if (!phone) return;

      this.refs.confirm.hidden = false;
      if (this.refs.leadBox) this.refs.leadBox.hidden = false;

      if (automated) {
        this.refs.confirm.innerHTML =
          '<span class="quiz__check" aria-hidden="true">✓</span><span>' +
          escapeHtml(this.t('lead.confirm_auto', { phone: phone.local })) + '</span>';
        return;
      }

      var waUrl = 'https://wa.me/' + this.config.whatsapp +
        '?text=' + encodeURIComponent(this.whatsappMessage(phone.intl));

      if (openWindow) {
        // Opened synchronously inside the click so mobile browsers allow it.
        var waWindow = window.open('', '_blank');
        if (waWindow) waWindow.location.href = waUrl;
      }

      this.refs.confirm.innerHTML =
        '<span class="quiz__check" aria-hidden="true">✓</span><span>' +
        escapeHtml(this.t('lead.confirm', { phone: phone.local })) +
        ' <a href="' + waUrl + '" target="_blank" rel="noopener">' +
        escapeHtml(this.t('lead.confirm_link')) + '</a>.</span>';
    }
  }

  if (!customElements.get('bluebell-quiz')) {
    customElements.define('bluebell-quiz', BluebellQuiz);
  }
})();
